import { Accordion } from '@retailer-platform/shared-components/src/tds'
import { useMemo, useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { useDashIntl } from '../../../intl/intl.hooks'
import { useNavHierarchyRetailer, useUserSettingsHierarchy } from '../nav-v2/NavMenuHierarchy'
import { useGetPermissionsQuery } from '../../../__codegen__/api'
import { useRetailerId } from '../../../utils/routing/params.hooks'
import { getDescendants, hasSelectedDescendants, trimTree } from './utils'
import { ToolPermissionSelectorL1Section } from './ToolPermissionSelectorL1Section'
import { useNonNavMenuPermissions } from './hooks/useNonNavMenuPermissions'

export const PermissionAccordion = ({
  onEnabledPermissionChange,
  enabledPermissionSlugs,
  // used when we only want to show an overview of which tools / permissions are enabled and hide all other options
  readOnly = false,
  // only show tools / permissions that are retailer-assignable
  retailerAssignableOnly,
  // only used when PermissionAccordion is used to manage permissions directly assigned to a user (not a role). We need to know
  // which permissions the user has been assigned via roles, so that we can show them as "hard enabled" - i.e. they cannot
  // be disabled
  userRoles,
}: {
  onEnabledPermissionChange?: (enabledPermissionSlugs: string[]) => void
  enabledPermissionSlugs: string[]
  readOnly?: boolean
  retailerAssignableOnly?: boolean
  userRoles?: { label: string; permissions: { slug: string }[] }[]
}) => {
  const { navMenuHierarchy } = useNavHierarchyRetailer(!!retailerAssignableOnly)
  const { navMenuHierarchy: userSettingsHierarchy } = useUserSettingsHierarchy()
  const retailerId = useRetailerId({ throwOnMissing: false })

  const { nonNavMenuPermissions } = useNonNavMenuPermissions()

  // used to gray out permissions when managing directly assigned permissions for a user
  // cannot edit permissions that are already assigned via roles
  const roleAssignedPermissionSlugs = useMemo(
    () =>
      new Set(
        userRoles?.flatMap(role => role?.permissions?.map(permission => permission.slug) || [])
      ),
    [userRoles]
  )

  const enabledPermissionSlugsSet = useMemo(
    () => new Set(enabledPermissionSlugs),
    [enabledPermissionSlugs]
  )

  // Fetches the list of all permissions attainable for the current user
  // We only allow users to assign permissions that are attainable for them
  const { data: attainablePermissions } = useGetPermissionsQuery({
    variables: { retailerId: retailerId?.length > 0 ? retailerId : undefined },
  })

  // if we are editing external users or external facing roles, we only display tools or
  // in-tool permissions that are retailer-assignable
  const allowedPermissionSlugs = useMemo(() => {
    const allowedPermissions = retailerAssignableOnly
      ? attainablePermissions?.permissions?.filter(
          permission => permission.isRetailerRoleAssignable
        )
      : attainablePermissions?.permissions

    return new Set(allowedPermissions?.map(permission => permission.slug))
  }, [attainablePermissions, retailerAssignableOnly])

  const navItems = useMemo(() => {
    const l1Items = cloneDeep([
      ...navMenuHierarchy.hierarchy,
      ...userSettingsHierarchy.hierarchy,
      ...nonNavMenuPermissions,
    ]).filter(item => item.name !== 'user.actions')

    // filter out any inToolPermissions that are not in allowedPermissionSlugs
    l1Items.forEach(item => {
      getDescendants(item).forEach(tool => {
        if (tool.inToolPermissions) {
          tool.inToolPermissions = tool.inToolPermissions.filter(permObj =>
            allowedPermissionSlugs.has(permObj.slug)
          )
        }
      })
    })

    // every tool can have one or more access control objects, and we may want
    // to filter some of these out For example, a tool might have
    // accessControl: [
    //    { permissions: ['internal_admin'] },
    //    { permissions: ['merchandising.modify'] }
    // ]
    // The first access control object is not assignable to external user (they
    // cannot be internal_admin), so we need to filter it out. This cleaned up
    // access control list is useful for later when we are determining which
    // permissions to assign to a user or role based on the the tool selected
    l1Items.forEach(item => {
      getDescendants(item).forEach(tool => {
        // filter out any access control objects that are not in allowedPermissionSlugs

        const accessControlArray = !Array.isArray(tool.accessControl)
          ? [tool.accessControl]
          : tool.accessControl

        const filteredAccessControl = accessControlArray.filter(accessControl => {
          const permissionsArray = !Array.isArray(accessControl.permissions)
            ? [accessControl.permissions]
            : accessControl.permissions

          return allowedPermissionSlugs.isSupersetOf(new Set(permissionsArray as string[]))
        })
        tool.accessControl = filteredAccessControl as any
      })
    })

    if (readOnly) {
      // lets remove all items that are not in the enabledPermissionSlugsSet
      l1Items.forEach(item => trimTree(item, enabledPermissionSlugsSet))
      return l1Items.filter(item => hasSelectedDescendants(item, enabledPermissionSlugsSet))
    } else {
      // lets remove all items that aren't in the allowedPermissionSlugs
      l1Items.forEach(item => trimTree(item, allowedPermissionSlugs))
      return l1Items.filter(item => hasSelectedDescendants(item, allowedPermissionSlugs))
    }
  }, [
    navMenuHierarchy.hierarchy,
    userSettingsHierarchy.hierarchy,
    enabledPermissionSlugsSet,
    readOnly,
    allowedPermissionSlugs,
    nonNavMenuPermissions,
  ])

  const dashIntl = useDashIntl()

  const [selectedPermissionSlugs, setSelectedPermissionSlugs] =
    useState<Set<string>>(enabledPermissionSlugsSet)

  const handlePermissionSlugsChange = (newSelectedPermissionSlugs: Set<string>) => {
    setSelectedPermissionSlugs(newSelectedPermissionSlugs)
    onEnabledPermissionChange?.(Array.from(newSelectedPermissionSlugs))
  }

  const translateNavLabel = (i18nKey: string) => {
    try {
      return dashIntl.formatMessage({ id: i18nKey as any })
    } catch (error) {
      return i18nKey // fallback to the key itself
    }
  }

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      {navItems.map(item => (
        <Accordion
          key={item.name}
          body={
            <ToolPermissionSelectorL1Section
              item={item}
              selectedPermissionSlugs={selectedPermissionSlugs}
              handlePermissionSlugsChange={handlePermissionSlugsChange}
              readOnly={readOnly}
              roleAssignedPermissionSlugs={roleAssignedPermissionSlugs}
              userRoles={userRoles}
            />
          }
          defaultExpandedLevels={{
            subtitle: hasSelectedDescendants(item, selectedPermissionSlugs),
            body: true,
          }}
          levels={2}
          showTitle={false}
          showDescription={false}
          subtitle={translateNavLabel(item.name)}
        />
      ))}
    </div>
  )
}
